import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiInfo, FiShield, FiGlobe, FiActivity, FiUsers, FiClock, FiDatabase, FiAlertTriangle } = FiIcons;

const AboutSection = () => {
  const features = [
    {
      icon: FiActivity,
      title: 'Real-time Monitoring',
      description: 'Live data feeds from PAGASA, PHIVOLCS, USGS, and GDACS provide up-to-the-minute information on seismic activity, volcanic eruptions, floods, tsunamis, and weather hazards.'
    },
    {
      icon: FiShield,
      title: 'Multi-Agency Integration',
      description: 'Aggregates data from multiple official monitoring agencies to provide comprehensive coverage of all types of natural hazards affecting the Philippines.'
    },
    {
      icon: FiGlobe,
      title: 'Interactive Mapping',
      description: 'Advanced GIS visualization shows hazard locations, fault lines, volcanic activity, and affected areas with detailed information for each event.'
    },
    {
      icon: FiAlertTriangle,
      title: 'Alert System',
      description: 'Color-coded warnings and real-time alerts help users quickly understand the severity of ongoing hazards and take appropriate action.'
    },
    {
      icon: FiDatabase,
      title: 'Historical Data',
      description: 'Access historical hazard data and trends to understand patterns and improve disaster preparedness and response planning.'
    },
    {
      icon: FiUsers,
      title: 'Emergency Resources',
      description: 'Comprehensive emergency contact information and resources for immediate assistance during disasters and emergencies.'
    },
  ];

  const dataSources = [
    {
      name: 'PHIVOLCS',
      fullName: 'Philippine Institute of Volcanology and Seismology',
      description: 'Monitors volcanic activity, earthquakes, and tsunamis in the Philippines',
      website: 'https://www.phivolcs.dost.gov.ph'
    },
    {
      name: 'PAGASA',
      fullName: 'Philippine Atmospheric, Geophysical and Astronomical Services Administration',
      description: 'Provides weather forecasts, tropical cyclone warnings, and flood monitoring',
      website: 'https://www.pagasa.dost.gov.ph'
    },
    {
      name: 'USGS',
      fullName: 'United States Geological Survey',
      description: 'Global earthquake monitoring and seismic data provider',
      website: 'https://earthquake.usgs.gov'
    },
    {
      name: 'GDACS',
      fullName: 'Global Disaster Alert and Coordination System',
      description: 'UN-led platform providing global disaster alerts and coordination',
      website: 'https://www.gdacs.org'
    },
  ];

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg shadow-md p-6 mb-6"
    >
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
          <SafeIcon icon={FiInfo} className="text-blue-600" />
          <span>About Philippines Disaster Risk Portal</span>
        </h2>
        <p className="text-gray-600 leading-relaxed mb-4">
          The Philippines Disaster Risk Portal is a comprehensive real-time monitoring dashboard designed to provide accurate, timely information about natural hazards affecting the Philippines. Located in the geologically active Pacific Ring of Fire region, the Philippines faces numerous natural threats including earthquakes, volcanic eruptions, floods, and tsunamis.
        </p>
        <p className="text-gray-600 leading-relaxed">
          This platform integrates data from multiple official sources to create a unified view of ongoing hazards, helping citizens, emergency responders, and government agencies make informed decisions during critical situations. Our mission is to save lives and reduce disaster impact through improved situational awareness and access to reliable information.
        </p>
      </div>

      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">Key Features</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-gray-50 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start space-x-3">
                <SafeIcon icon={feature.icon} className="text-blue-600 text-xl mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">{feature.title}</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">Data Sources</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {dataSources.map((source) => (
            <div key={source.name} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-semibold text-gray-900">{source.name}</h4>
                  <p className="text-xs text-gray-500 mb-2">{source.fullName}</p>
                  <p className="text-sm text-gray-600 mb-3">{source.description}</p>
                </div>
              </div>
              <a
                href={source.website}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 transition-colors"
              >
                <span>Visit Official Site</span>
                <SafeIcon icon={FiGlobe} className="ml-1 text-xs" />
              </a>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-2">
          <SafeIcon icon={FiClock} className="text-blue-600 text-sm mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <strong>Important Note:</strong> This platform provides information for situational awareness only. For official warnings and emergency instructions, always follow guidance from PAGASA, PHIVOLCS, NDRRMC, and your local government units. During emergencies, prioritize official evacuation orders and safety protocols.
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default AboutSection;